import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Alert,
} from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { useCreateContact } from '../../hooks/useCrm';
import { TagPicker } from '../tags/TagPicker';
import { apiClient } from '../../lib/api';

interface SimpleContactModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (contact: any) => void;
}

export const SimpleContactModal: React.FC<SimpleContactModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const createContactMutation = useCreateContact();

  // Fetch all tags
  const { data: availableTags = [], refetch: refetchTags } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await apiClient.getTags();
      return response.data;
    },
  });

  // Handle creating new tags
  const handleCreateTag = async (name: string, color: string) => {
    const response = await apiClient.createTag(name, color);
    await refetchTags();
    return response.data;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const contact = await createContactMutation.mutateAsync({
        ...formData,
        tag_ids: selectedTagIds,
      });

      onSuccess?.(contact);

      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
      });
      setSelectedTagIds([]);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    }
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    });
    setSelectedTagIds([]);
    setError(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: '90vw', maxWidth: 500 }}>
        <ModalClose />
        <Typography level="h4" sx={{ mb: 2 }}>
          Add New Contact
        </Typography>
        
        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl required>
              <FormLabel>First Name</FormLabel>
              <Input
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                disabled={createContactMutation.isPending}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Last Name</FormLabel>
              <Input
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                disabled={createContactMutation.isPending}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={createContactMutation.isPending}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                disabled={createContactMutation.isPending}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Tags</FormLabel>
              {/* label="" — TagPicker renders its own "Tags" label by default,
                  which doubled up with the FormLabel above */}
              <TagPicker
                label=""
                value={selectedTagIds}
                onChange={setSelectedTagIds}
                availableTags={availableTags}
                onCreateTag={handleCreateTag}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                color="neutral"
                onClick={handleClose}
                disabled={createContactMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="solid"
                color="primary"
                loading={createContactMutation.isPending}
              >
                Create Contact
              </Button>
            </Box>
          </Box>
        </form>
      </ModalDialog>
    </Modal>
  );
};